import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CdkTreeModule } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { PipesModule } from '../theme/pipes/pipes.module';
import { DirectivesModule } from '../theme/directives/directives.module'; 
import { HeaderImageComponent } from './header-image/header-image.component';
import { PropertyItemComponent } from './property-item/property-item.component';
import { PropertiesSearchResultsFiltersComponent } from './properties-search-results-filters/properties-search-results-filters.component';
import { PropertiesCarouselComponent } from './properties-carousel/properties-carousel.component';
import { MissionComponent } from './mission/mission.component';
import { OurServicesComponent } from './our-services/our-services.component';
import { LogoComponent } from './logo/logo.component';
import { ConfirmationDialogComponent } from './dialog/confirmation-dialog.component';
import { AuthorizationStripeComponent } from './dialog/auction-authorization/stripe/authorization-stripe.component';
import { AuthorizationDialogPaypalComponent } from './dialog/auction-authorization/paypal/authorization-dialog-paypal.component';
import { AuthorizationDialogPlaidComponent } from './dialog/auction-authorization/plaid/authorization-dialog-plaid.component';
import { BuyNowDialogComponent } from './dialog/buy-now-dialog.component';
import { MakeOfferDialogComponent } from './dialog/make-offer-dialog.component';
import { PlaceBidDialogComponent } from './dialog/place-bid-dialog.component';
import { RepresentBuyerDialogComponent } from './dialog/represent-buyer-dialog.component';
import { LegalInformationPropertyEditComponent } from './dialog/sub-components/legal-information-property-edit.component';
import { PropertiesSearchHomeComponent } from './properties-search-box/properties-search-box.component';
import { ConfirmRegistrationDialogComponent } from './dialog/confirm-registration-dialog.component';
import { BiddingHistoryDialogComponent } from './dialog/bidding-history-dialog.component';
import { InputFileModule } from './input-file/input-file.module';
import { InputFileConfig } from './input-file/interfaces/input-file-config';
import { UserContactDialogComponent } from './dialog/user-contact-dialog.component';
import { NewsletterComponent } from './newsletter/newsletter.component';
import { CountdownTimerComponent } from './countdown-timer/countdown-timer.component';
import { DocumentUploadDialogComponent } from './dialog/document-upload-dialog.component';
import { DocumentsTableComponent } from './documents-table/documents-table.component';
import { EditAddressDialogComponent } from './dialog/edit-address-dialog.component';
import { AdditionalAddressesTableComponent } from './additional-addresses-table/additional-addresses-table.component';
import { MFADialogComponent } from './dialog/mfa-dialog.component';
import { MFARecoveryDialogComponent } from './dialog/mfa-recovery-dialog.component';
import { FooterComponent } from '../theme/components/footer/footer.component';
import { InformationDialogComponent } from './dialog/information-dialog.component';
import { UnitMixTableComponent } from './unit-mix-table/unit-mix-table.component';
import { VerifyUserDialogComponent } from './dialog/verify-user-dialog.component';
import { AuctionAuthorizationDialogComponent } from './dialog/auction-authorization/auction-authorization-dialog.component';
import { PrivateAuctionAuthorizationDialogComponent } from './dialog/private-auction-authorization/private-auction-authorization-dialog.component';
import { BuyerLegalDocumentComponent } from './dialog/sub-components/buyer-legal-document.component';
import { BiddingHistoryAdminComponent } from './bidding-history-admin/bidding-history-admin.component';
import { BiddingHistoryPublicComponent } from './bidding-history-public/bidding-history-public.component';
import { BiddingHistoryAnonymizedComponent } from './bidding-history-anonymized/bidding-history-anonymized.component';
import { RepresentationsTableComponent } from './representations-table/representations-table.component';
import { RepresentationsDialogComponent } from './dialog/representations-dialog.component';
import { PropertyFilterComponent } from './properties-filter/property-filter.component';
import { AuctionTypeFilterComponent } from './properties-filter/auction-type-filter/auction-type-filter.component';
import { PropertyTypeFilterComponent } from './properties-filter/property-type-filter/property-type-filter.component';
import { StateFilterComponent } from './properties-filter/state-filter/state-filter.component';
import { SaleTypeFilterComponent } from './properties-filter/sale-type-filter/sale-type-filter.component';
import { PropertyFeaturesFilterComponent } from './properties-filter/property-features-filter/property-features-filter.component';
import { AllFiltersComponent } from './properties-filter/all-filters/all-filters.component';
import { AvatarComponent } from './avatar/avatar.component';
import { HotEditDialogComponent } from './dialog/hot-edit-dialog.component';
import { BPSpreadTooltipModule } from './bp-spread-tooltip/bp-spread-tooltip.module';
import { EditReserveCreditDialogComponent } from './dialog/edit-reserve-credit-dialog.component';
import { PropertyViewsComponent } from './property-views/property-views.component';
import { ComboInputComponent } from '../theme/components/combo-input/combo-input.component';
import { EditableControlComponent } from '../theme/components/editable-control/editable-control.component';
import { EditableSelectComponent } from '../theme/components/editable-select/editable-select.component';
import { PropertyAbstractComponent } from './property-abstract/property-abstract.component';
import { ExpansionPanelComponent } from '../theme/components/expansion-panel/expansion-panel.component';
import { CheckboxOptionGroupComponent } from '../theme/components/checkbox-option-group/checkbox-option-group.component';
import { EditableDatetimeControlComponent } from '../theme/components/editable-datetime-control/editable-datetime-control.component';
import { SquareIconButtonComponent } from '../theme/components/square-icon-button/square-icon-button.component';
import { SearchbarComponent } from '../theme/components/searchbar/searchbar.component';
import { VaultDocumentSelectComponent } from '../theme/components/vault-document-select/vault-document-select.component';
import { MultiSelectComponent } from '../theme/components/multi-select/multi-select.component';
import { ImageUploadComponent } from '../theme/components/image-upload/image-upload.component';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { CountdownComponent } from 'ngx-countdown';
import { DashboardPropertyComponent } from '../pages/account/dashboard/dashboard-property/dashboard-property.component';
import { StateCountySelectorComponent } from '../theme/components/state-county-selector/state-county-selector.component';
import { EntityNotesComponent } from '../theme/components/entity-notes/entity-notes.component';
import { ListingImageComponent } from './listing-image/listing-image.component';

const config: InputFileConfig = {
  fileAccept: '*'
};

@NgModule({
  imports: [
    CdkTreeModule,
    CdkScrollableModule,
    CommonModule,
    DragDropModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    MatStepperModule,
    PipesModule,
    DirectivesModule,
    InputFileModule.forRoot(config),
    ListingImageComponent,
    BPSpreadTooltipModule,
    CountdownComponent,
    ComboInputComponent,
    EditableControlComponent,
    EditableDatetimeControlComponent,
    EditableSelectComponent,
    ExpansionPanelComponent,
    CheckboxOptionGroupComponent,
    SquareIconButtonComponent,
    SearchbarComponent,
    VaultDocumentSelectComponent,
    MultiSelectComponent,
    ImageUploadComponent,
    StateCountySelectorComponent,
  ],
  exports: [
    CdkTreeModule,
    DragDropModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    MatStepperModule,
    PipesModule,
    DirectivesModule, 
    LogoComponent,
    HeaderImageComponent,
    PropertyItemComponent,
    PropertiesSearchResultsFiltersComponent,
    PropertiesCarouselComponent,
    MissionComponent, 
    OurServicesComponent,
    ConfirmationDialogComponent,
    AuthorizationStripeComponent,
    AuthorizationDialogPaypalComponent,
    AuthorizationDialogPlaidComponent,
    BuyNowDialogComponent,
    MakeOfferDialogComponent,
    PlaceBidDialogComponent,
    RepresentBuyerDialogComponent,
    LegalInformationPropertyEditComponent,
    PropertiesSearchHomeComponent,
    ConfirmRegistrationDialogComponent,
    BiddingHistoryDialogComponent,
    BiddingHistoryAdminComponent,
    BiddingHistoryPublicComponent,
    BiddingHistoryAnonymizedComponent,
    UserContactDialogComponent,
    InputFileModule,
    NewsletterComponent,
    CountdownTimerComponent,
    DocumentUploadDialogComponent,
    DocumentsTableComponent,
    EditAddressDialogComponent,
    AdditionalAddressesTableComponent,
    MFADialogComponent,
    MFARecoveryDialogComponent,
    InformationDialogComponent,
    FooterComponent,
    UnitMixTableComponent,
    VerifyUserDialogComponent,
    AuctionAuthorizationDialogComponent,
    PrivateAuctionAuthorizationDialogComponent,
    BuyerLegalDocumentComponent,
    RepresentationsTableComponent,
    RepresentationsDialogComponent,
    AuctionTypeFilterComponent,
    PropertyFilterComponent,
    PropertyTypeFilterComponent,
    StateFilterComponent,
    SaleTypeFilterComponent,
    PropertyFeaturesFilterComponent,
    AllFiltersComponent,
    AvatarComponent,
    HotEditDialogComponent,
    EditReserveCreditDialogComponent,
    BPSpreadTooltipModule,
    PropertyViewsComponent,
    ComboInputComponent,
    EditableControlComponent,
    EditableDatetimeControlComponent,
    EditableSelectComponent,
    ExpansionPanelComponent,
    PropertyAbstractComponent,
    CheckboxOptionGroupComponent,
    SquareIconButtonComponent,
    SearchbarComponent,
    VaultDocumentSelectComponent,
    MultiSelectComponent,
    ImageUploadComponent,
    DashboardPropertyComponent,
    StateCountySelectorComponent,
    EntityNotesComponent,
    ListingImageComponent
  ],
  declarations: [ 
    LogoComponent,
    HeaderImageComponent,
    PropertyItemComponent,
    PropertiesSearchResultsFiltersComponent,
    PropertiesCarouselComponent,
    MissionComponent, 
    OurServicesComponent,
    ConfirmationDialogComponent,
    AuthorizationStripeComponent,
    AuthorizationDialogPaypalComponent,
    AuthorizationDialogPlaidComponent,
    BuyNowDialogComponent,
    MakeOfferDialogComponent,
    PlaceBidDialogComponent,
    RepresentBuyerDialogComponent,
    LegalInformationPropertyEditComponent,
    PropertiesSearchHomeComponent,
    ConfirmRegistrationDialogComponent,
    BiddingHistoryDialogComponent,
    BiddingHistoryAdminComponent,
    BiddingHistoryPublicComponent,
    BiddingHistoryAnonymizedComponent,
    UserContactDialogComponent,
    NewsletterComponent,
    CountdownTimerComponent,
    DocumentUploadDialogComponent,
    DocumentsTableComponent,
    EditAddressDialogComponent,
    AdditionalAddressesTableComponent,
    MFADialogComponent,
    MFARecoveryDialogComponent,
    InformationDialogComponent,
    FooterComponent,
    UnitMixTableComponent,
    VerifyUserDialogComponent,
    AuctionAuthorizationDialogComponent,
    PrivateAuctionAuthorizationDialogComponent,
    BuyerLegalDocumentComponent,
    RepresentationsTableComponent,
    RepresentationsDialogComponent,
    PropertyFilterComponent,
    AuctionTypeFilterComponent,
    PropertyTypeFilterComponent,
    StateFilterComponent,
    SaleTypeFilterComponent,
    PropertyFeaturesFilterComponent,
    AllFiltersComponent,
    AvatarComponent,
    HotEditDialogComponent,
    EditReserveCreditDialogComponent,
    PropertyViewsComponent,
    PropertyAbstractComponent,
    DashboardPropertyComponent,
    EntityNotesComponent
  ],
  providers:[],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule { 
}
